'use client';

import React, { useState } from 'react';

// Theme Constants matching your Facilities Section
const GOLD_ACCENT = '#faaf40';
const SHADOW_GOLD = 'rgba(250, 175, 64, 0.6)';

export default function JoinForm() {
  // 1. State handles all input fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    designation: '',
    goal: 'General Fitness & Conditioning',
  });
  
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('Submitting...');

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('Welcome to the Club! We will contact you shortly.');

        // ==========================================
        // 2. FACEBOOK PIXEL TRACKING CODE ADDED HERE
        // ==========================================
        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', 'Lead', {
                content_name: 'Gym Membership Form', 
                content_category: 'EMS & Pilates',
                value: 0.00,
                currency: 'PKR',
                city: formData.city,            // Sends the city entered by user
                service_type: formData.goal     // Sends the goal selected
            }, {
                // Advanced Matching for better ad targeting
                em: formData.email,             
                ph: formData.phone,             
                fn: formData.name,              
                ct: formData.city               
            });
        }
        // ==========================================

        // Reset form
        setFormData({ 
            name: '', 
            email: '', 
            phone: '', 
            city: '', 
            designation: '', 
            goal: 'General Fitness & Conditioning' 
        });
      } else {
        setStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setStatus('Error submitting form.');
    } finally {
      setLoading(false);
    }
  };

  // Shared Input Styles
  const inputClasses = `
    w-full bg-[#1a1a1a] text-white border border-white/20 rounded-lg 
    px-4 py-3 outline-none transition-all duration-300
    focus:border-[color:var(--gold)] focus:ring-1 focus:ring-[color:var(--gold)]
    placeholder-gray-500
  `;

  const labelClasses = "block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wide";

  return (
    <section 
      className="py-16 sm:py-24 bg-gradient-to-b from-black via-[#0a0a0a] to-black text-white relative overflow-hidden"
      style={{ '--gold': GOLD_ACCENT, '--shadow-gold': SHADOW_GOLD }}
    >
      {/* Decorative blurred glow behind the form */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-10 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${GOLD_ACCENT} 0%, transparent 70%)` }}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 relative z-10">
        
        {/* Header Section */}
        <div className="text-center mb-10">
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 uppercase italic" 
            style={{ color: GOLD_ACCENT, textShadow: `0 0 15px ${SHADOW_GOLD}` }}
          >
            Start Your Journey
          </h2>
          <p className="text-gray-400 text-base sm:text-lg">
            Exclusive memberships are limited. Secure your spot today.
          </p>
        </div>

        {/* Form Card */}
        <form 
          onSubmit={handleSubmit} 
          className="bg-black/80 backdrop-blur-md border border-white/10 p-6 sm:p-10 rounded-2xl shadow-2xl"
          style={{ boxShadow: `0 0 20px -5px rgba(0,0,0,0.8)` }}
        >
          
          <div className="space-y-6">
            
            {/* Name Input */}
            <div>
              <label htmlFor="name" className={labelClasses}>Full Name</label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="ENTER YOUR FULL NAME"
                className={inputClasses}
              />
            </div>
            
            {/* Email Input */}
            <div>
              <label htmlFor="email" className={labelClasses}>Email Address</label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="ENTER YOUR EMAIL"
                className={inputClasses}
              />
            </div>

            {/* Phone Input */}
            <div>
              <label htmlFor="phone" className={labelClasses}>Phone Number</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+92 XXX - XXXXXXX"
                className={inputClasses}
              />
            </div>

            {/* --- CITY & DESIGNATION --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* City Input */}
                <div>
                  <label htmlFor="city" className={labelClasses}>City</label>
                  <input
                    id="city"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="ISLAMABAD"
                    className={inputClasses}
                  />
                </div>

                {/* Designation Input */}
                <div>
                  <label htmlFor="designation" className={labelClasses}>Profession / Job</label>
                  <input
                    id="designation"
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleChange}
                    placeholder="E.G. DOCTOR"
                    className={inputClasses}
                  />
                </div>
            </div>

            {/* Dropdown Goal Selection */}
            <div>
              <label htmlFor="goal" className={labelClasses}>Your Goal</label>
              <div className="relative">
                <select
                  id="goal"
                  name="goal"
                  value={formData.goal}
                  onChange={handleChange}
                  className={`${inputClasses} appearance-none cursor-pointer`}
                >
                  <option value="General Fitness & Conditioning">General Fitness & Conditioning</option>
                  <option value="Weight Loss">Weight Loss</option>
                  <option value="Weight Gain / Muscle Building">Muscle Building</option>
                  <option value="EMS Training">EMS Training</option>
                  <option value="Pilates">Pilates</option>
                </select>
                {/* Custom Chevron Arrow */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--gold)]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={loading}
              className="w-full font-bold text-black uppercase tracking-wider py-4 rounded-lg mt-4 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: GOLD_ACCENT,
                boxShadow: `0 0 15px ${SHADOW_GOLD}` 
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Join Now"
              )}
            </button>
          </div>

          {/* Status Message */}
          {status && (
            <div className={`mt-6 text-center text-sm font-medium p-3 rounded border ${status.includes('Welcome') ? 'border-green-500/30 text-green-400 bg-green-500/10' : 'border-red-500/30 text-red-400 bg-red-500/10'}`}>
              {status}
            </div>
          )}

        </form>
      </div>
    </section>
  );
}